import React from "react";

/**
 * Props for the StatusBadge component.
 * Allows displaying the connection presence state of a registered computer.
 */
export interface StatusBadgeProps {
  /**
   * Current online status of the computer.
   * - "online": Device is actively sending heartbeats (< 30s ago).
   * - "idle": Device is active but idle (no input/activity).
   * - "offline": Device is offline (no heartbeats in the last 30s).
   */
  status: "online" | "idle" | "offline";
  /**
   * Optional inline styles to customize layout placement or margins.
   */
  style?: React.CSSProperties;
  /**
   * Optional custom CSS class name.
   */
  className?: string;
}

/**
 * StatusBadge Component
 *
 * Renders a compact, visually distinct indicator badge for computer presence status.
 * Driven by the presence status enums: "online" (green), "idle" (yellow/orange), and "offline" (red).
 * Designed for a dark-mode UI with smooth glow highlights and inline styles.
 *
 * For non-coders:
 * - Modify `statusConfig` values below to adjust labels, border thickness, text colors, or translucent background opacities.
 * - Change the dot size by tweaking the width/height under the `dotStyle` block.
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, style, className = "" }) => {
  // Config definition containing colors and labels for each presence state.
  // Non-coders: Adjust hex color codes, background opacity (rgba), and borders to change theme colors!
  const statusConfig = {
    online: {
      label: "Online",
      color: "var(--color-success, #10B981)", // Green color to represent active online status
      bg: "rgba(16, 185, 129, 0.08)", // 8% opacity green background fill
      borderColor: "rgba(16, 185, 129, 0.25)", // 25% opacity green border
      shadow: "0 0 6px rgba(16, 185, 129, 0.2)", // Subtle green outer glow
    },
    idle: {
      label: "Idle",
      color: "var(--color-warning, #F59E0B)", // Amber color to represent idle presence
      bg: "rgba(245, 158, 11, 0.08)", // 8% opacity amber background fill
      borderColor: "rgba(245, 158, 11, 0.25)", // 25% opacity amber border
      shadow: "0 0 6px rgba(245, 158, 11, 0.2)", // Subtle amber outer glow
    },
    offline: {
      label: "Offline",
      color: "var(--color-error, #EF4444)", // Red color to represent offline state
      bg: "rgba(239, 68, 68, 0.08)", // 8% opacity red background fill
      borderColor: "rgba(239, 68, 68, 0.25)", // 25% opacity red border
      shadow: "none", // No shadow glow for offline status to keep it low profile
    },
  };

  // Safe fallback to "offline" in case status string is invalid, undefined, or an inherited object prototype key.
  // This keeps the UI robust even if a new database state is introduced.
  // Non-coders: If you want to change the fallback state (e.g. to "idle"), change "offline" below.
  const resolvedStatus = (status === "online" || status === "idle" || status === "offline") ? status : "offline";

  const current = statusConfig[resolvedStatus];

  // Outer container styling representing the badge capsule.
  // Feel free to modify padding, border radius, letter-spacing, etc.
  const containerStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem", // Space between status dot and text label
    padding: "0.25rem 0.65rem",
    borderRadius: "var(--border-radius-sm, 4px)",
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: current.color,
    backgroundColor: current.bg,
    border: `1px solid ${current.borderColor}`,
    boxShadow: current.shadow,
    transition: "all var(--transition-speed, 0.2s) ease",
    whiteSpace: "nowrap",
    ...style, // Allow merging with caller styles
  };

  // Status dot indicator styling (glow dot).
  // Non-coders: Increase width/height here (e.g. "8px") if you want a larger indicator dot.
  const dotStyle: React.CSSProperties = {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: current.color,
    boxShadow: `0 0 8px ${current.color}`, // Glow highlight using the status color
    display: "inline-block",
  };

  return (
    <span
      style={containerStyle}
      className={`status-badge status-${resolvedStatus} ${className}`}
      data-testid="status-badge"
    >
      <span 
        style={dotStyle} 
        data-testid="status-badge-dot" 
        className="status-badge-dot"
      />
      <span 
        data-testid="status-badge-label" 
        className="status-badge-label"
      >
        {current.label}
      </span>
    </span>
  );
};

export default StatusBadge;
